import {
  GetAccountDetailsQuery,
  GetTeamDetailsQuery,
  ProjectRolesUpdate,
} from '@pypestream/api-services/urql';
import { ManagerContext, ManagerEvents } from './manager.xstate.types';
import { assign as immerAssign } from '@xstate/immer';
import { isEqual, sortBy } from 'lodash';
import {
  AgentAssistRoleNames,
  AnalyticsRoleNames,
  productsSortFunction,
} from './permission-details-xstate-helper';
import { ProductName } from '@pypestream/api-services';
import {
  AccountProject,
  ProjectProductRoles,
} from '../components/permissions-table/permissions-table.types';

export type TeamDetailsType = NonNullable<
  GetTeamDetailsQuery['tenancy_teams_by_pk']
>;

export type TeamUsersType = NonNullable<GetTeamDetailsQuery['tenancy_users']>;

export const NEW_TEAM_TEMP_ID = 'new-team';

export type TeamDetailUpdates = {
  hasPendingUpdates: boolean;
  projectIdsToAdd: string[];
  projectIdsToRemove: string[];
  roleIdsToAdd: string[];
  roleIdsToRemove: string[];
  userIdsToAdd: string[];
  userIdsToRemove: string[];
  projectRolesUpdate: ProjectRolesUpdate[];
};

export const teamDetailUpdateInitialState: TeamDetailUpdates = {
  hasPendingUpdates: false,
  projectIdsToAdd: [],
  projectIdsToRemove: [],
  roleIdsToAdd: [],
  roleIdsToRemove: [],
  userIdsToAdd: [],
  userIdsToRemove: [],
  projectRolesUpdate: [],
};

export const teamDetailsInitialState: Omit<TeamDetailsType, 'canManageAccess'> =
  {
    id: '',
    name: '',
    description: '',
    usersFromCurrentOrg: [],
    usersFromOtherOrgs: [],
    team_accounts: [],
  };

export const getTeamPrimaryAccountId = (teamDetails: GetTeamDetailsQuery) => {
  return (
    teamDetails.tenancy_teams_by_pk?.team_accounts[0].isPrimaryAccount &&
    teamDetails.tenancy_teams_by_pk?.team_accounts[0].accountId
  );
};

export const getProjectAvailableRoles = (
  projectProductSettings: AccountProject['project']['project_product_settings'],
  assignedProjectRoleIds: string[],
  allOrgRoles: NonNullable<
    NonNullable<
      NonNullable<GetAccountDetailsQuery['admin_']>['accountById']
    >['roles']
  >['rows']
): NonNullable<ProjectProductRoles> => {
  const projectAvailableRoles: NonNullable<ProjectProductRoles> = [];

  projectProductSettings.forEach((product) => {
    const availableProductRoles = allOrgRoles?.filter(
      (role) =>
        role.productId === product.product.id &&
        !assignedProjectRoleIds.includes(role.id as string)
    );
    if (availableProductRoles) {
      projectAvailableRoles.push(...availableProductRoles);
    }
  });

  return projectAvailableRoles;
};

export const getTeamAccountProjectRoles = (
  currentProject: AccountProject,
  projectRolesUpdate: ProjectRolesUpdate[]
): AccountProject['_account_project_roles'] => {
  return currentProject._account_project_roles.reduce(
    (accumulatedRoles, currentRole) => {
      const sameProductRoleIdx = accumulatedRoles?.findIndex(
        (roles) => roles.role.productId === currentRole.role.productId
      );
      const sameProductRole = accumulatedRoles[sameProductRoleIdx];

      if (sameProductRoleIdx == null || sameProductRoleIdx < 0) {
        accumulatedRoles.push(currentRole);
        return accumulatedRoles;
      }

      const existingProjectRoleUpdateIndex = projectRolesUpdate.findIndex(
        (project) => project.id === currentProject.project.id
      );
      // If role is highest possible role for product, then keep that role assigned and remove other low level roles.
      if (
        currentRole.role.name === AgentAssistRoleNames.AGENT_ASSIST_ADMIN ||
        currentRole.role.name === AnalyticsRoleNames.ANALYTICS_ADMIN
      ) {
        // make this role available with availableProjectRoles
        currentProject.availableProjectRoles?.push({
          id: sameProductRole.role.id,
          name: sameProductRole.role.name,
          productId: sameProductRole.role.productId,
        });
        if (existingProjectRoleUpdateIndex >= 0) {
          projectRolesUpdate[
            existingProjectRoleUpdateIndex
          ].rolesToRemove!.push(sameProductRole.role.id);
        } else {
          projectRolesUpdate.push({
            id: currentProject.project.id,
            rolesToRemove: [sameProductRole.role.id],
            rolesToAdd: [],
          });
        }
        accumulatedRoles[sameProductRoleIdx] = currentRole;
      } else {
        currentProject.availableProjectRoles?.push({
          id: currentRole.role.id,
          name: currentRole.role.name,
          productId: currentRole.role.productId,
          product: {
            id: currentRole.role.product?.id,
            name: currentRole.role.product?.name as ProductName,
            displayName: currentRole.role.product?.displayName,
          },
        });
        if (existingProjectRoleUpdateIndex >= 0) {
          projectRolesUpdate[
            existingProjectRoleUpdateIndex
          ].rolesToRemove!.push(currentRole.role.id);
        } else {
          projectRolesUpdate.push({
            id: currentProject.project.id,
            rolesToRemove: [currentRole.role.id],
            rolesToAdd: [],
          });
        }
      }

      return accumulatedRoles;
    },
    [] as AccountProject['_account_project_roles']
  );
};

export const discardTeamChanges = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.teamDetails.discardUpdates' || !event.teamId)
      return ctx;

    if (ctx.teamDetails[event.teamId]) {
      ctx.teamDetails[event.teamId].state =
        ctx.teamDetails[event.teamId].initialState;
      ctx.teamDetails[event.teamId].teamDetailUpdates =
        teamDetailUpdateInitialState;
    }
    return ctx;
  }
);

export const clearTeamState = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.teamDetails.clearTeamState' || !event.teamId) {
      return ctx;
    }
    delete ctx.teamDetails[event.teamId];
    return ctx;
  }
);

export const addUsersToTeam = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.teamDetails.addUsersToTeam' || !event.teamId) {
      return ctx;
    }
    const teamDetails = ctx.teamDetails[event.teamId];
    if (!teamDetails) return ctx;

    const { state, teamDetailUpdates } = teamDetails;

    event.userIds.forEach((addUserId) => {
      const availableUser = state.availableUsers.find(
        (user) => user.id === addUserId
      );

      if (!availableUser) return;

      if (teamDetailUpdates.userIdsToRemove.includes(addUserId)) {
        teamDetailUpdates.userIdsToRemove =
          teamDetailUpdates.userIdsToRemove.filter(
            (userId) => userId !== addUserId
          );
      } else if (!teamDetailUpdates.userIdsToAdd.includes(addUserId)) {
        teamDetailUpdates.userIdsToAdd = [
          ...teamDetailUpdates.userIdsToAdd,
          addUserId,
        ];
      }

      state.availableUsers = state.availableUsers.filter(
        (user) => addUserId !== user.id
      );

      if (
        !state.usersFromCurrentOrg.find(
          (userTeam) => userTeam.user.id === addUserId
        )
      ) {
        state.usersFromCurrentOrg = [
          ...state.usersFromCurrentOrg,
          { user: availableUser },
        ];
      }
    });

    teamDetailUpdates.hasPendingUpdates = hasPendingUpdates(teamDetailUpdates);

    return ctx;
  }
);

export const removeUserFromTeam = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.teamDetails.removeUserFromTeam' ||
      !event.teamId
    ) {
      return ctx;
    }
    const teamDetails = ctx.teamDetails[event.teamId];
    if (!teamDetails) return ctx;

    const { state, teamDetailUpdates } = teamDetails;
    const removeUserId = event.userId;

    const teamUser = state.usersFromCurrentOrg.find(
      (user) => user.user.id === removeUserId
    );
    if (!teamUser) return ctx;

    if (teamDetailUpdates.userIdsToAdd.includes(removeUserId)) {
      teamDetailUpdates.userIdsToAdd = teamDetailUpdates.userIdsToAdd.filter(
        (userId) => userId !== removeUserId
      );
    } else if (!teamDetailUpdates.userIdsToRemove.includes(removeUserId)) {
      teamDetailUpdates.userIdsToRemove = [
        ...teamDetailUpdates.userIdsToRemove,
        removeUserId,
      ];
    }

    state.usersFromCurrentOrg = state.usersFromCurrentOrg.filter(
      (user) => user.user.id !== removeUserId
    );

    if (!state.availableUsers.find((user) => user.id === removeUserId)) {
      state.availableUsers = [...state.availableUsers, teamUser.user];
      state.availableUsers = sortBy(
        state.availableUsers,
        'firstName',
        'lastName'
      );
    }

    teamDetailUpdates.hasPendingUpdates = hasPendingUpdates(teamDetailUpdates);

    return ctx;
  }
);

export const toggleTeamAllOrgsCollapseState = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      !ctx.selectedTeam ||
      event.type !== 'manager.teamDetails.toggleAllOrgsCollapseState'
    ) {
      return;
    }

    ctx.teamDetails[ctx.selectedTeam]?.state?.orgs?.forEach(
      (org) => (org.isCollapsed = event.collapse)
    );

    ctx.teamDetails[ctx.selectedTeam]?.initialState?.orgs?.forEach(
      (org) => (org.isCollapsed = event.collapse)
    );
  }
);

export const toggleTeamOrgCollapseState = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      !ctx.selectedTeam ||
      event.type !== 'manager.teamDetails.toggleOrgCollapseState'
    ) {
      return;
    }

    const currentTeamDetails = ctx.teamDetails[ctx.selectedTeam];
    const orgIndex = currentTeamDetails?.state?.orgs?.findIndex(
      (org) => org.id === event.orgId
    );

    if (orgIndex != null && orgIndex >= 0) {
      currentTeamDetails.state.orgs[orgIndex].isCollapsed = event.collapse;
      currentTeamDetails.initialState.orgs[orgIndex].isCollapsed =
        event.collapse;
    }
  }
);

export const handleAddProjectToTeam = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.teamDetails.addProject' || !ctx.selectedTeam)
      return;

    const { project } = event;
    const allOrgs = ctx.teamDetails[ctx.selectedTeam]?.state?.orgs || [];
    const orgIndexToUpdate = allOrgs.findIndex(
      (org) => org.id === project.accountId
    );

    if (orgIndexToUpdate < 0) return;

    const orgAvailableRoles = allOrgs[orgIndexToUpdate]?.roles?.rows;
    const { projectProductSettings } = project;
    // Recreating project_product_settings object to align types with 2 different sources.
    const project_product_settings: AccountProject['project']['project_product_settings'] =
      [];

    // Calculate all available roles for the project
    const availableProjectRoles = projectProductSettings?.reduce(
      (allProductRoles, currentProduct) => {
        orgAvailableRoles
          ?.filter((role) => role.productId === currentProduct.productId)
          .forEach((orgProductRole) => {
            allProductRoles?.push({
              id: orgProductRole.id,
              name: orgProductRole.name,
              productId: orgProductRole.productId,
              product: orgProductRole.product,
            });
          });

        // Aligning types with 2 different sources of project_product_settings.
        project_product_settings.push({
          product: {
            name: currentProduct.product?.name || '',
            id: currentProduct.product?.id || '',
            displayName: currentProduct.product?.displayName || '',
          },
        });

        return allProductRoles;
      },
      [] as ProjectProductRoles
    );

    project_product_settings.sort(productsSortFunction);

    const projectToAdd: AccountProject = {
      id: project.id,
      availableProjectRoles,
      _account_project_roles: [],
      project: {
        id: project.id,
        name: project.name,
        accountId: project.accountId,
        project_product_settings,
      },
    };

    // Add project to org object's assignedProjects property.
    allOrgs[orgIndexToUpdate].assignedProjects?.push(projectToAdd);

    // Remove project from org object's availableProjects property.
    allOrgs[orgIndexToUpdate].availableProjects = allOrgs[
      orgIndexToUpdate
    ].availableProjects?.filter(
      (availableProject) => availableProject.id !== project.id
    );

    // Recalculate rowSpan property for org.
    const canAddMoreProjects =
      (allOrgs[orgIndexToUpdate].availableProjects?.length || 0) > 0;

    allOrgs[orgIndexToUpdate].orgRowSpan =
      (allOrgs[orgIndexToUpdate].orgRowSpan || 1) +
      Math.max(projectToAdd.project.project_product_settings.length - 1, 0) +
      ((canAddMoreProjects && 1) || 0);

    ctx.teamDetails[ctx.selectedTeam]?.teamDetailUpdates?.projectIdsToAdd?.push(
      project.id
    );

    ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates);
    return ctx;
  }
);

export const updateTeamProjectProductRole = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.teamDetails.updateProjectProductRole' ||
      !ctx.selectedTeam
    )
      return;

    const { projectId, orgId, role, operation } = event;
    const orgToUpdate = ctx.teamDetails[ctx.selectedTeam]?.state?.orgs.find(
      (org) => org.id === orgId
    );
    const projectToUpdate = orgToUpdate?.assignedProjects?.find(
      (project) => project.project.id === projectId
    );

    if (!orgToUpdate || !projectToUpdate) return;

    const { teamDetailUpdates } = ctx.teamDetails[ctx.selectedTeam];
    const existingProjectRoleUpdateIndex =
      teamDetailUpdates.projectRolesUpdate.findIndex(
        (_projectRoleUpdate) => _projectRoleUpdate.id === projectId
      );

    const projectRoleUpdate: ProjectRolesUpdate = {
      id: projectId,
      rolesToRemove:
        (existingProjectRoleUpdateIndex >= 0 &&
          teamDetailUpdates.projectRolesUpdate?.[existingProjectRoleUpdateIndex]
            ?.rolesToRemove) ||
        [],
      rolesToAdd:
        (existingProjectRoleUpdateIndex >= 0 &&
          teamDetailUpdates.projectRolesUpdate?.[existingProjectRoleUpdateIndex]
            ?.rolesToAdd) ||
        [],
    };

    projectToUpdate._account_project_roles.forEach(
      (_team_account_project_role) => {
        if (
          _team_account_project_role.role.productId !== role.productId ||
          projectToUpdate.availableProjectRoles?.find(
            (availableRole) =>
              availableRole.id === _team_account_project_role.role.id
          )
        )
          return;

        projectToUpdate.availableProjectRoles?.push({
          id: _team_account_project_role.role.id,
          name: _team_account_project_role.role.name || '',
          productId: _team_account_project_role.role.productId || '',
          product: {
            id: _team_account_project_role.role.product?.id || '',
            name: (_team_account_project_role.role.product?.name ||
              '') as ProductName,
            displayName:
              _team_account_project_role.role.product?.displayName || '',
          },
        });

        if (
          !projectRoleUpdate.rolesToAdd?.includes(
            _team_account_project_role.role.id
          )
        ) {
          projectRoleUpdate.rolesToRemove?.push(
            _team_account_project_role.role.id
          );
        } else {
          projectRoleUpdate.rolesToAdd =
            projectRoleUpdate.rolesToAdd?.filter(
              (roleId) => roleId !== _team_account_project_role.role.id
            ) || [];
        }
      }
    );

    projectToUpdate._account_project_roles =
      projectToUpdate._account_project_roles.filter(
        (_team_account_project_role) =>
          _team_account_project_role.role.productId !== role.productId
      );

    if (operation === 'add' || operation === 'update') {
      projectToUpdate._account_project_roles.push({
        role: {
          id: role.id || '',
          name: role.name || '',
          productId: role.productId || '',
          product: {
            name: role.product?.name || '',
            id: role.product?.id || '',
            displayName: role.product?.displayName || '',
          },
        },
      });
      projectToUpdate.availableProjectRoles =
        projectToUpdate.availableProjectRoles?.filter(
          (availableProjectRole) => availableProjectRole.id !== role.id
        );
      if (
        !projectRoleUpdate.rolesToAdd?.includes(role.id || '') &&
        !projectRoleUpdate.rolesToRemove?.includes(role.id || '')
      ) {
        projectRoleUpdate.rolesToAdd?.push(role.id || '');
      } else {
        projectRoleUpdate.rolesToRemove =
          projectRoleUpdate.rolesToRemove?.filter(
            (roleId) => roleId !== role.id
          ) || [];
      }
    }

    const projectRolesUpdateCtx =
      ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates.projectRolesUpdate;

    if (existingProjectRoleUpdateIndex >= 0) {
      projectRolesUpdateCtx[existingProjectRoleUpdateIndex] = projectRoleUpdate;
    } else {
      projectRolesUpdateCtx.push(projectRoleUpdate);
    }

    ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates.projectRolesUpdate =
      projectRolesUpdateCtx.filter(
        (roleUpdate) =>
          (roleUpdate.rolesToAdd || []).length > 0 ||
          (roleUpdate.rolesToRemove || []).length > 0
      );

    ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates);

    return ctx;
  }
);

export const updateTeamManagerRole = immerAssign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.teamDetails.updateManagerRole' ||
      !ctx.selectedTeam
    )
      return;
    const { orgId, role: roleToUpdate, operation } = event;
    const orgToUpdate = ctx.teamDetails[ctx.selectedTeam]?.state?.orgs.find(
      (org) => org.id === orgId
    );
    if (!orgToUpdate || !orgToUpdate.accountRoles) return;
    const { teamDetailUpdates } = ctx.teamDetails[ctx.selectedTeam];

    if (operation === 'add') {
      orgToUpdate.accountRoles?.push(roleToUpdate);
      orgToUpdate.availableAccountRoles =
        orgToUpdate.availableAccountRoles?.filter(
          (availableRole) => availableRole.role.id !== roleToUpdate.role.id
        );

      if (teamDetailUpdates.roleIdsToRemove.includes(roleToUpdate.role.id)) {
        teamDetailUpdates.roleIdsToRemove =
          teamDetailUpdates.roleIdsToRemove.filter(
            (roleId) => roleId !== roleToUpdate.role.id
          );
      } else {
        teamDetailUpdates.roleIdsToAdd.push(roleToUpdate.role.id);
      }
    } else {
      const roleToRemove =
        orgToUpdate.accountRoles &&
        orgToUpdate.accountRoles.length > 0 &&
        orgToUpdate.accountRoles[0];
      orgToUpdate.accountRoles = [];

      if (operation === 'update' && roleToRemove) {
        orgToUpdate.accountRoles?.push(roleToUpdate);
        orgToUpdate.availableAccountRoles =
          orgToUpdate.availableAccountRoles?.filter(
            (availableRole) => availableRole.role.id !== roleToUpdate.role.id
          );
        orgToUpdate.availableAccountRoles?.push({
          role: {
            id: roleToRemove.role.id,
            name: roleToRemove.role.name,
            productId: roleToRemove.role.productId,
            product: {
              id: roleToRemove.role.productId,
              name: roleToRemove.role.product?.name,
              displayName: roleToRemove.role.product?.displayName,
            },
            accountId: roleToRemove.role.accountId,
          },
        });

        if (
          isEqual(teamDetailUpdates.roleIdsToAdd, [roleToRemove.role.id]) &&
          isEqual(teamDetailUpdates.roleIdsToRemove, [roleToUpdate.role.id])
        ) {
          // If updating roles, going back to default state, then ignore the updates with selectedUserDetailUpdates
          teamDetailUpdates.roleIdsToAdd = [];
          teamDetailUpdates.roleIdsToRemove = [];
        } else {
          teamDetailUpdates.roleIdsToAdd?.push(roleToUpdate.role.id);
          teamDetailUpdates.roleIdsToRemove?.push(roleToRemove.role.id);

          if (teamDetailUpdates.roleIdsToAdd?.includes(roleToRemove.role.id)) {
            teamDetailUpdates.roleIdsToRemove =
              teamDetailUpdates.roleIdsToRemove?.filter(
                (id) => id !== roleToRemove.role.id
              );
          }

          teamDetailUpdates.roleIdsToAdd =
            teamDetailUpdates.roleIdsToAdd?.filter(
              (id) => id !== roleToRemove.role.id
            );
          teamDetailUpdates.roleIdsToRemove =
            teamDetailUpdates.roleIdsToRemove?.filter(
              (id) => id !== roleToUpdate.role.id
            );
        }
      } else if (operation === 'remove') {
        orgToUpdate.availableAccountRoles?.push({
          role: {
            id: roleToUpdate.role.id || '',
            name: roleToUpdate.role.name || '',
            productId: roleToUpdate.role.productId || '',
            product: {
              id: roleToUpdate.role.productId || '',
              name: roleToUpdate.role.product?.name || '',
              displayName: roleToUpdate.role.product?.displayName || '',
            },
            accountId: roleToUpdate.role.accountId || '',
          },
        });

        if (teamDetailUpdates.roleIdsToAdd?.includes(roleToUpdate.role.id)) {
          teamDetailUpdates.roleIdsToAdd =
            teamDetailUpdates.roleIdsToAdd?.filter(
              (roleId) => roleId !== roleToUpdate.role.id
            );
        } else {
          teamDetailUpdates.roleIdsToRemove?.push(roleToUpdate.role.id);
        }
      }
    }

    orgToUpdate.availableAccountRoles = sortBy(
      orgToUpdate.availableAccountRoles,
      'role.name'
    );

    // We need to update the accountRoles for all orgs, as accountRoles cascade down
    ctx.teamDetails[ctx.selectedTeam].state.orgs = ctx.teamDetails[
      ctx.selectedTeam
    ].state.orgs.map((org) => ({
      ...org,
      accountRoles: orgToUpdate.accountRoles,
    }));

    ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates = teamDetailUpdates;
    ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(ctx.teamDetails[ctx.selectedTeam].teamDetailUpdates);
  }
);

export const hasPendingUpdates = (
  teamDetailUpdates: TeamDetailUpdates
): boolean => {
  return (
    (teamDetailUpdates.userIdsToAdd || []).length > 0 ||
    (teamDetailUpdates.userIdsToRemove || []).length > 0 ||
    (teamDetailUpdates.projectIdsToAdd || []).length > 0 ||
    (teamDetailUpdates.projectIdsToRemove || []).length > 0 ||
    (teamDetailUpdates.roleIdsToAdd || []).length > 0 ||
    (teamDetailUpdates.roleIdsToRemove || []).length > 0 ||
    (teamDetailUpdates.projectRolesUpdate || []).length > 0
  );
};
